import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Drawer, IconButton } from '@mui/material';
import { CloseRounded } from '@mui/icons-material';
import { Button } from '@qlub-dev/qlub-kit';
import { useVendor } from '@clubpay/customer-common-module/src/context/vendor';
import { useTranslation } from '@clubpay/customer-common-module/src/hook/translations';
import {
    DiningOptionEnum,
    ScheduleInteractionFlowEnum,
} from '@clubpay/customer-common-module/src/repository/vendor/type';
import { useQlubRouter } from '@clubpay/customer-common-module/src/hook/router';
import QSRDiningOptions from '@/components/QSR/QSROrderOptionModalDineType';
import QSRPickUpOptions from 'components/QSR/QSRPickUpOptions';
import QSRScheduledOrderModal from '@/components/QSR/QSRScheduledOrderPicker';
import { IScheduledTime, ScheduledOrderEnum } from '@/components/QSR/QSRScheduledOrder';
import QSRConfirmChangeModal from '@/components/QSR/QSRBasketResetModal';
import useQsrEventsCenter from '@/hooks/qsrEventsCenter';
import { useBasket } from '@/contexts/basket';
import { createDateBasedOnDay } from '@/services/utils/k_qsr';
import { IBasket } from '@/repositories/menu/types';

import styles from './index.module.scss';

enum OriginEnum {
    FirstLoad = 'firstLoad',
    Anchor = 'anchor',
}

enum StepEnum {
    DineOption = 'dineOption',
    PickupOption = 'pickupOption',
    Schedule = 'schedule',
}

export enum OptionModalSourceEnum {
    MenuPage = 'menu_page',
    OrderDetailsPage = 'order_details_page',
}

interface IProps {
    source: OptionModalSourceEnum;
}

const QSRDiningOptionModal = ({ source }: IProps) => {
    const { t } = useTranslation('common');
    const { vendor } = useVendor();
    const { basket, setBasket } = useBasket();
    const { routerPush } = useQlubRouter();

    const [open, setOpen] = useState(false);
    const [origin, setOrigin] = useState<OriginEnum>(OriginEnum.FirstLoad);
    const [step, setStep] = useState<StepEnum>(StepEnum.DineOption);

    const [confirmOpen, setConfirmOpen] = useState(false);

    const [dineOption, setDineOption] = useState<DiningOptionEnum | undefined>(undefined);
    const [pickUpOption, setPickUpOption] = useState<ScheduledOrderEnum | undefined>(undefined);
    const [scheduleAt, setScheduleAt] = useState<IScheduledTime | undefined>(undefined);

    const {
        qsr: { qsrScheduleTypeSelected, qsrAsapTypeSelected },
    } = useQsrEventsCenter();

    const diningOptionEnable = vendor?.orderConfig?.diningOptionEnable || false;
    const scheduledOrderV2Enable =
        (vendor?.orderConfig?.scheduledOrderEnable &&
            vendor?.orderConfig?.scheduleInteractionFlow === ScheduleInteractionFlowEnum.V2) ||
        false;

    useEffect(() => {
        if (!diningOptionEnable || basket?.diningOption) {
            return;
        }

        setOpen(true);
        setOrigin(OriginEnum.FirstLoad);
        setStep(StepEnum.DineOption);
    }, [basket]);

    useEffect(() => {
        const handleOpenDrawer = () => {
            setOpen(true);
            setOrigin(OriginEnum.Anchor);
            if (basket) {
                if (diningOptionEnable) {
                    setDineOption(basket.diningOption);
                }
                if (scheduledOrderV2Enable) {
                    setScheduleAt({
                        type: basket.scheduledAt ? ScheduledOrderEnum.Scheduled : ScheduledOrderEnum.ASAP,
                    });
                    setPickUpOption(basket.scheduledAt ? ScheduledOrderEnum.Scheduled : ScheduledOrderEnum.ASAP);
                }
            }
        };

        window.addEventListener('openDiningOptionDrawer', handleOpenDrawer);
        return () => {
            window.removeEventListener('openDiningOptionDrawer', handleOpenDrawer);
        };
    }, [basket, diningOptionEnable, scheduledOrderV2Enable]);

    const handleClose = useCallback(
        (e, reason?: any) => {
            if (origin === OriginEnum.FirstLoad && ['backdropClick', 'escapeKeyDown'].includes(reason)) {
                return;
            }

            setOpen(false);
        },
        [origin],
    );

    const saveHandler = (val?: { scheduleAt?: IScheduledTime; skipCheck?: boolean; dineOption?: DiningOptionEnum }) => {
        const finalScheduleAt = val?.scheduleAt || scheduleAt;
        const partialBasket: Partial<IBasket> = {
            scheduledAt:
                pickUpOption === ScheduledOrderEnum.Scheduled && finalScheduleAt
                    ? createDateBasedOnDay(finalScheduleAt.value?.date, finalScheduleAt.value?.timePeriod?.start)
                    : undefined,
            ...(dineOption && { diningOption: val?.dineOption || dineOption }),
        };

        const currentPartialBasket: Partial<IBasket> = {
            scheduledAt: basket.scheduledAt,
            ...(dineOption && { diningOption: dineOption }),
        };

        if (
            JSON.stringify(partialBasket) === JSON.stringify(currentPartialBasket) &&
            pickUpOption !== ScheduledOrderEnum.ASAP &&
            scheduledOrderV2Enable
        ) {
            setOpen(false);
            return;
        }

        if (!val?.skipCheck && basket.items?.length) {
            setConfirmOpen(true);
            return;
        }

        setBasket({
            ...basket,
            items: [],
            ...partialBasket,
        });

        setDineOption(undefined);
        setPickUpOption(undefined);
        setScheduleAt(undefined);

        setOpen(false);
    };

    useEffect(() => {
        if (origin === OriginEnum.FirstLoad) {
            if (step === StepEnum.DineOption && dineOption) {
                if (!scheduledOrderV2Enable) {
                    saveHandler();
                    return;
                }

                setStep(StepEnum.PickupOption);
                return;
            }

            if (step === StepEnum.PickupOption && pickUpOption) {
                if (pickUpOption !== ScheduledOrderEnum.Scheduled) {
                    qsrAsapTypeSelected();
                    saveHandler();
                    return;
                }
                qsrScheduleTypeSelected();
                setStep(StepEnum.Schedule);
                return;
            }

            if (step === StepEnum.Schedule && scheduleAt) {
                saveHandler();
            }
        }
    }, [dineOption, pickUpOption, scheduleAt, step, origin, scheduledOrderV2Enable]);

    const inlineContent = useMemo(() => {
        return (
            <div className={styles.scheduleContent}>
                <div className={styles.header}>{t('Schedule order details')}</div>

                {diningOptionEnable && (
                    <QSRDiningOptions
                        inline
                        value={dineOption}
                        onChange={(val) => {
                            setDineOption(val);
                        }}
                    />
                )}

                {scheduledOrderV2Enable && (
                    <>
                        <QSRPickUpOptions
                            inline
                            diningOption={dineOption}
                            value={pickUpOption}
                            onChange={(val) => {
                                setPickUpOption(val);
                            }}
                        />

                        {pickUpOption === ScheduledOrderEnum.Scheduled ? (
                            <QSRScheduledOrderModal
                                value={scheduleAt}
                                onChange={(val) => {
                                    setScheduleAt(val);
                                    saveHandler({ scheduleAt: val });
                                }}
                            />
                        ) : (
                            <Button
                                className={styles.confirmBtn}
                                variant="contained"
                                onClick={() => {
                                    saveHandler();
                                }}
                            >
                                {t('Confirm')}
                            </Button>
                        )}
                    </>
                )}
            </div>
        );
    }, [dineOption, pickUpOption, scheduleAt, t]);

    const content = useMemo(() => {
        switch (step) {
            case StepEnum.PickupOption:
                return (
                    <QSRPickUpOptions
                        diningOption={dineOption}
                        value={pickUpOption}
                        onChange={(val) => {
                            setPickUpOption(val);
                        }}
                    />
                );
            case StepEnum.Schedule:
                return (
                    <div className={styles.scheduleContent}>
                        <div className={styles.header}>{t('Schedule order details')}</div>
                        <QSRScheduledOrderModal
                            value={scheduleAt}
                            onChange={(val) => {
                                setScheduleAt(val);
                            }}
                        />
                    </div>
                );
            case StepEnum.DineOption:
            default:
                return (
                    <QSRDiningOptions
                        value={dineOption}
                        onChange={(val) => {
                            if (!scheduledOrderV2Enable) {
                                saveHandler({ dineOption: val });
                            }
                            setDineOption(val);
                        }}
                    />
                );
        }
    }, [step, dineOption, pickUpOption, scheduleAt, t]);

    const handleQSRLandingRedirect = () => {
        routerPush('/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/qsr');
    };

    const basketResetConfirmHandler = useCallback(() => {
        saveHandler({
            skipCheck: true,
        });
        setConfirmOpen(false);
        if (source === OptionModalSourceEnum.OrderDetailsPage) {
            handleQSRLandingRedirect();
        }
    }, [saveHandler]);

    const basketResetCloseHandler = useCallback(() => {
        setConfirmOpen(false);
    }, []);

    return (
        <Drawer
            variant="temporary"
            anchor="bottom"
            disablePortal
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    maxWidth: '552px',
                    margin: '0 auto',
                    background: '#fff',
                    borderRadius: '30px 30px 0 0',
                    padding: '24px',
                },
            }}
        >
            {origin === OriginEnum.Anchor && (
                <IconButton onClick={handleClose} className={styles.closeIcon}>
                    <CloseRounded />
                </IconButton>
            )}
            {origin === OriginEnum.FirstLoad || !scheduledOrderV2Enable ? content : inlineContent}
            <QSRConfirmChangeModal
                open={confirmOpen}
                onClose={basketResetCloseHandler}
                onConfirm={basketResetConfirmHandler}
            />
        </Drawer>
    );
};

export default QSRDiningOptionModal;
